import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Upload,
  X,
  Calendar as CalendarIcon,
  FileText,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const PatientMoreInfoPanel = ({ 
  isOpen, 
  onClose, 
  customFieldValues = [], 
  onValuesChange,
  t 
}) => {
  const [fieldsByGroup, setFieldsByGroup] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [localValues, setLocalValues] = useState({});
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      fetchFieldsByGroup();
    }
  }, [isOpen]);

  useEffect(() => {
    // Initialize local values from props
    const values = {};
    customFieldValues.forEach(cfv => {
      values[cfv.field_id] = cfv.value;
    });
    setLocalValues(values);
  }, [customFieldValues]);

  const fetchFieldsByGroup = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patient-custom-fields/fields/by-group`);
      setFieldsByGroup(response.data);
      // Auto-expand all groups
      const expanded = {};
      response.data.forEach(item => {
        expanded[item.group.id] = true;
      });
      setExpandedGroups(expanded);
    } catch (error) {
      console.error('Error fetching fields by group:', error);
      toast.error('Error loading custom fields');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleValueChange = (fieldId, value) => {
    const newValues = { ...localValues, [fieldId]: value };
    setLocalValues(newValues);
    
    // Convert to array format for parent
    const valuesArray = Object.entries(newValues)
      .filter(([_, v]) => v !== undefined && v !== '')
      .map(([field_id, value]) => ({ field_id, value }));
    onValuesChange(valuesArray);
  };

  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [fieldId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/patient-custom-fields/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      handleValueChange(fieldId, {
        filename: response.data.filename,
        file_url: response.data.file_url,
        size: response.data.size
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Error uploading file');
    } finally {
      setUploading(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const removeFile = (fieldId) => {
    handleValueChange(fieldId, null);
  };

  const renderField = (field) => {
    const value = localValues[field.id];

    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={field.placeholder}
          />
        );

      case 'long_text':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value ? parseFloat(e.target.value) : '')}
            placeholder={field.placeholder}
          />
        );

      case 'date':
      case 'date_picker':
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !value && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(new Date(value), 'PPP') : (field.placeholder || 'Select date')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={value ? new Date(value) : undefined}
                onSelect={(date) => handleValueChange(field.id, date?.toISOString())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );

      case 'dropdown':
        return (
          <Select value={value || ''} onValueChange={(v) => handleValueChange(field.id, v)}>
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={value === true}
              onCheckedChange={(checked) => handleValueChange(field.id, checked)}
            />
            <label htmlFor={field.id} className="text-sm text-muted-foreground cursor-pointer">
              {field.placeholder || field.description || 'Yes'}
            </label>
          </div>
        );

      case 'file_upload':
        return (
          <div className="space-y-2">
            {value?.filename ? (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                <FileText className="h-4 w-4 text-primary" />
                <span className="text-sm flex-1 truncate">{value.filename}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFile(field.id)}
                  className="h-6 w-6"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="file"
                  ref={(el) => fileInputRefs.current[field.id] = el}
                  className="hidden"
                  accept={field.allowed_file_types?.join(',') || '*'}
                  onChange={(e) => handleFileUpload(field.id, e.target.files?.[0])}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => fileInputRefs.current[field.id]?.click()}
                  disabled={uploading[field.id]}
                >
                  {uploading[field.id] ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {uploading[field.id] ? 'Uploading...' : 'Upload File'}
                </Button>
                {field.allowed_file_types?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Allowed: {field.allowed_file_types.join(', ')} (Max {field.max_file_size_mb}MB)
                  </p>
                )}
              </div>
            )}
          </div>
        );

      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={field.placeholder}
          />
        );
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[40vw] sm:max-w-[40vw] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            {t?.('customFields.moreInformation') || 'More Information'}
          </SheetTitle>
          <SheetDescription>
            {t?.('customFields.moreInfoDescription') || 'Fill in additional information for this patient.'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : fieldsByGroup.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t?.('customFields.noFieldsConfigured') || 'No custom fields configured yet.'}</p>
              <p className="text-sm mt-2">
                {t?.('customFields.goToSettings') || 'Go to Patients > Custom Fields to add fields.'}
              </p>
            </div>
          ) : (
            fieldsByGroup.map(({ group, fields }) => (
              <Collapsible
                key={group.id}
                open={expandedGroups[group.id]}
                onOpenChange={() => toggleGroup(group.id)}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      {expandedGroups[group.id] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="font-semibold">{group.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({fields.length} {fields.length === 1 ? 'field' : 'fields'})
                      </span>
                    </div>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4 pl-4 space-y-4">
                  {fields.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <Label className="flex items-center gap-1">
                        {field.name}
                        {field.required && <span className="text-destructive">*</span>}
                      </Label>
                      {field.description && (
                        <p className="text-xs text-muted-foreground">{field.description}</p>
                      )}
                      {renderField(field)}
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PatientMoreInfoPanel;
